import Decimal from 'decimal.js-light';
import { formatUnits } from 'viem';

import { tokenFormatter } from '@/lib/formatting';

import type { TokenUsdPrices } from '@/components/TransactionModule/tradeQuote';

import { solana } from '@/constants/chain';

import { canonicalSolanaMint } from '@/utils/multichain';
import { isSameTokenAddress } from '@/utils/normalizeAddress';

import type { Fee, Fees } from './0x.types';
import type { TokenResult } from './0x/token-registry.types';

export type TradeFlow = 'swap' | 'gasless' | 'intents';

export type FeeLineItem = {
  kind: 'gasRebate' | 'matcha' | 'matchaAuto' | 'integrator';
  label: string;
  formattedAmount?: string;
  token?: TokenResult;
};

export const isFeeInToken = (
  feeTokenAddress: string | undefined,
  token: Pick<TokenResult, 'address' | 'chainId'>,
): boolean => {
  const normalizedFeeTokenAddress =
    token.chainId === solana.id && feeTokenAddress
      ? canonicalSolanaMint(feeTokenAddress)
      : feeTokenAddress;
  const normalizedTokenAddress =
    token.chainId === solana.id ? canonicalSolanaMint(token.address) : token.address;

  return isSameTokenAddress(normalizedFeeTokenAddress, normalizedTokenAddress);
};

const resolveFeeToken = (fee: Fee, sellToken: TokenResult, buyToken: TokenResult): TokenResult =>
  isFeeInToken(fee.token, sellToken) ? sellToken : buyToken;

const formatFeeAmount = (fee: Fee, token: TokenResult): string =>
  tokenFormatter.format(formatUnits(BigInt(fee.amount), Number(token.decimals)), {
    precision: 3,
    symbol: token.symbol,
  });

const deriveVolumeFeePercent = (
  fee: Fee,
  sellToken: TokenResult,
  sellAmount: string | undefined,
  buyAmount: string | undefined,
  fees: Fees | undefined,
): number | undefined => {
  if (fee.type !== 'volume') return undefined;

  const isSellTokenFee = isFeeInToken(fee.token, sellToken);
  const volumeRaw = isSellTokenFee ? sellAmount : buyAmount;
  if (!volumeRaw) return undefined;

  let volumeAmount = new Decimal(volumeRaw);
  if (volumeAmount.isZero()) return undefined;

  if (!isSellTokenFee) {
    // buyAmount is net of fees; restore same-token deductions to recover the fee basis.
    const feeToken = { address: fee.token, chainId: sellToken.chainId };
    for (const deduction of [fees?.zeroExFee, fees?.integratorFee, fees?.gasFee]) {
      if (deduction && isFeeInToken(deduction.token, feeToken)) {
        volumeAmount = volumeAmount.plus(deduction.amount);
      }
    }
  }

  return new Decimal(fee.amount).div(volumeAmount).mul(100).toNumber();
};

export const calculateTotalFeePercent = ({
  fees,
  sellToken,
  sellAmount,
  buyAmount,
  suppressIntegratorFee = false,
}: {
  fees: Fees | undefined;
  sellToken: TokenResult;
  sellAmount?: string;
  buyAmount?: string;
  suppressIntegratorFee?: boolean;
}): number | undefined => {
  if (!fees) return undefined;

  let total = 0;
  const feesList = [fees.zeroExFee, suppressIntegratorFee ? null : fees.integratorFee];

  for (const fee of feesList) {
    if (!fee || BigInt(fee.amount) === 0n) continue;
    const percent = deriveVolumeFeePercent(fee, sellToken, sellAmount, buyAmount, fees);
    if (percent === undefined) return undefined;
    total += percent;
  }

  return total;
};

export const formatFeePercent = (percent: number): string => {
  const rounded = parseFloat(percent.toFixed(2));
  return `${rounded > 0 || percent === 0 ? rounded : percent.toPrecision(1)}%`;
};

/**
 * Normalizes the `fees` object of a 0x price/quote response into display-ready
 * line items. The same `zeroExFee` field means "Matcha Fee" on the standard
 * swap flow and "Matcha Auto Fee" on the gasless flow; `flow` disambiguates.
 * Percentages are derived from the response amounts where possible;
 * `fallbackMatchaAutoFeePercent` covers gasless fees that cannot be derived.
 */
export const parseFees = ({
  fees,
  flow,
  sellToken,
  buyToken,
  sellAmount,
  buyAmount,
  swapFeeBps,
  integratorName,
  fallbackMatchaAutoFeePercent,
}: {
  fees: Fees | undefined;
  flow: TradeFlow;
  sellToken: TokenResult;
  buyToken: TokenResult;
  sellAmount?: string;
  buyAmount?: string;
  swapFeeBps?: number;
  integratorName?: string;
  fallbackMatchaAutoFeePercent?: number;
}): FeeLineItem[] => {
  const items: FeeLineItem[] = [];

  // 'gasless' and 'intents' are both the Matcha Auto flow; fee display is identical.
  const isMatchaAuto = flow === 'gasless' || flow === 'intents';

  if (isMatchaAuto && fees?.gasFee) {
    const token = resolveFeeToken(fees.gasFee, sellToken, buyToken);
    items.push({
      kind: 'gasRebate',
      label: 'Network Cost Rebate',
      formattedAmount: formatFeeAmount(fees.gasFee, token),
      token,
    });
  }

  const zeroExFee = fees?.zeroExFee;
  if (isMatchaAuto) {
    if (!zeroExFee) {
      items.push({ kind: 'matchaAuto', label: 'Matcha Auto Fee (0%)' });
    } else {
      const token = resolveFeeToken(zeroExFee, sellToken, buyToken);
      const percent =
        deriveVolumeFeePercent(zeroExFee, sellToken, sellAmount, buyAmount, fees) ??
        fallbackMatchaAutoFeePercent;
      items.push({
        kind: 'matchaAuto',
        label: `Matcha Auto Fee${percent !== undefined ? ` (${formatFeePercent(percent)})` : ''}`,
        formattedAmount: formatFeeAmount(zeroExFee, token),
        token,
      });
    }
  } else if (zeroExFee) {
    const token = resolveFeeToken(zeroExFee, sellToken, buyToken);
    const percent = deriveVolumeFeePercent(zeroExFee, sellToken, sellAmount, buyAmount, fees);
    items.push({
      kind: 'matcha',
      label: `Matcha Fee${percent !== undefined ? ` (${formatFeePercent(percent)})` : ''}`,
      formattedAmount: formatFeeAmount(zeroExFee, token),
      token,
    });
  }

  if (swapFeeBps && integratorName) {
    const integratorFee = fees?.integratorFee;
    const token = integratorFee ? resolveFeeToken(integratorFee, sellToken, buyToken) : undefined;
    const percent =
      (integratorFee
        ? deriveVolumeFeePercent(integratorFee, sellToken, sellAmount, buyAmount, fees)
        : undefined) ?? swapFeeBps / 100;
    items.push({
      kind: 'integrator',
      label: `${integratorName} Fee (${formatFeePercent(percent)})`,
      formattedAmount: integratorFee && token ? formatFeeAmount(integratorFee, token) : undefined,
      token,
    });
  }

  return items;
};

export const calculateZeroExFeeInUsd = (
  zeroExFee: Fee | null | undefined,
  sellToken: Pick<TokenResult, 'address' | 'chainId' | 'decimals'>,
  buyToken: Pick<TokenResult, 'address' | 'chainId' | 'decimals'>,
  tokenUsdPrices: TokenUsdPrices,
): number => {
  if (!zeroExFee) return 0;

  const isSellToken = isFeeInToken(zeroExFee.token, sellToken);
  const decimals = Number(isSellToken ? sellToken.decimals : buyToken.decimals);
  if (!decimals) return 0;

  const usdPrice = isSellToken ? tokenUsdPrices.sellTokenUsd : tokenUsdPrices.buyTokenUsd;
  return parseFloat(formatUnits(BigInt(zeroExFee.amount), decimals)) * usdPrice;
};
